// src/components/templates/diary/DiaryCalendarView.jsx - UPDATED
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Clock, 
  MapPin, Users, Video, Building2, Filter, X, Eye, MoreHorizontal,
  Maximize2, Minimize2, RefreshCw, Grid, List, CalendarPlus,
  ListTodo, CalendarCheck, Ticket, Briefcase, CalendarClock
} from 'lucide-react';
import { Button, Badge, MeetingStatus } from './UIComponents';

// View modes
const ViewMode = {
  MONTH: 'MONTH',
  WEEK: 'WEEK',
  DAY: 'DAY'
};

const MonthDisplayMode = {
  GRID: 'GRID',
  LIST: 'LIST'
};

// Planner item types
const ItemType = {
  TASK: 'TASK',
  EVENT_HOSTING: 'EVENT_HOSTING',
  EVENT_ATTENDING: 'EVENT_ATTENDING',
  BOOKING_PROVIDER: 'BOOKING_PROVIDER',
  BOOKING_CUSTOMER: 'BOOKING_CUSTOMER'
};

// --- PLANNER QUICK ADD MENU ---
const PlannerQuickAddMenu = ({ isOpen, onClose, onSelect, position }) => {
  if (!isOpen) return null;

  const options = [
    { type: ItemType.TASK, label: 'Task', icon: ListTodo, color: 'indigo' },
    { type: ItemType.EVENT_HOSTING, label: 'Host Event', icon: CalendarPlus, color: 'purple' },
    { type: ItemType.EVENT_ATTENDING, label: 'Event to Attend', icon: Ticket, color: 'pink' },
    { type: ItemType.BOOKING_PROVIDER, label: 'Client Booking', icon: Briefcase, color: 'green' },
    { type: ItemType.BOOKING_CUSTOMER, label: 'Book Service', icon: CalendarClock, color: 'teal' },
  ];

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div 
        className="absolute z-50 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2"
        style={{ 
          top: position?.top || 0, 
          left: position?.left || 0
        }}
      >
        {options.map(opt => (
          <button
            key={opt.type}
            onClick={() => { onSelect(opt.type); onClose(); }}
            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors"
          >
            <div className={`p-2 rounded-lg bg-${opt.color}-100`}>
              <opt.icon className={`w-4 h-4 text-${opt.color}-600`} />
            </div>
            <span className="font-medium text-gray-700">{opt.label}</span>
          </button>
        ))}
      </div>
    </>
  );
};

// --- MEETING POPUP ---
const MeetingPopup = ({ meeting, onSelect, onClose, position }) => {
  if (!meeting) return null;

  const statusColors = {
    [MeetingStatus.ACTIVE]: 'bg-green-500',
    [MeetingStatus.COMPLETED]: 'bg-gray-400',
    [MeetingStatus.PLANNED]: 'bg-indigo-500',
  };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div 
        className="absolute z-50 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
        style={{ 
          top: position?.top || 0, 
          left: position?.left || 0,
          transform: 'translate(-50%, 8px)'
        }}
      >
        <div className={`h-1.5 ${statusColors[meeting.status] || statusColors[MeetingStatus.PLANNED]}`} />
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-bold text-gray-900 line-clamp-2">{meeting.title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
              {new Date(meeting.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2 text-gray-400" />
              {meeting.time} ({meeting.durationMinutes}m)
            </div>
            {meeting.location && (
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                <span className="truncate">{meeting.location}</span>
              </div>
            )}
            <div className="flex items-center">
              {meeting.type === 'IN_PERSON' ? (
                <><Building2 className="w-4 h-4 mr-2 text-gray-400" /> In Person</>
              ) : (
                <><Video className="w-4 h-4 mr-2 text-gray-400" /> Virtual</>
              )}
            </div>
            {meeting.participants?.length > 0 && (
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-2 text-gray-400" />
                {meeting.participants.length} participants
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between">
            <Badge color={
              meeting.status === MeetingStatus.PLANNED ? 'blue' :
              meeting.status === MeetingStatus.ACTIVE ? 'green' : 'gray'
            }>{meeting.status}</Badge>
            <button 
              onClick={() => { onSelect(meeting); onClose(); }}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center"
            >
              Open <Eye className="w-3 h-3 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// --- DAY STATS ---
const DayStats = ({ meetings, plannerItems = [] }) => {
  const stats = useMemo(() => {
    const planned = meetings.filter(m => m.status === MeetingStatus.PLANNED).length;
    const active = meetings.filter(m => m.status === MeetingStatus.ACTIVE).length;
    const completed = meetings.filter(m => m.status === MeetingStatus.COMPLETED).length;
    const totalMinutes = meetings.reduce((sum, m) => sum + (m.durationMinutes || 0), 0);
    return { planned, active, completed, total: meetings.length, totalMinutes, plannerTotal: plannerItems.length };
  }, [meetings, plannerItems]);

  if (stats.total === 0 && stats.plannerTotal === 0) return null;

  return (
    <div className="flex items-center gap-3 text-xs flex-wrap">
      {stats.total > 0 && (
        <>
          <span className="px-2 py-1 bg-indigo-100 rounded-full text-indigo-700 font-medium">
            {stats.total} meeting{stats.total !== 1 ? 's' : ''}
          </span>
          <span className="px-2 py-1 bg-gray-100 rounded-full text-gray-600">
            {Math.floor(stats.totalMinutes / 60)}h {stats.totalMinutes % 60}m
          </span>
          {stats.active > 0 && (
            <span className="px-2 py-1 bg-green-100 rounded-full text-green-700 font-medium">
              {stats.active} active
            </span>
          )}
        </>
      )}
      {stats.plannerTotal > 0 && (
        <span className="px-2 py-1 bg-emerald-100 rounded-full text-emerald-700 font-medium">
          {stats.plannerTotal} planner item{stats.plannerTotal !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
};

// --- MAIN COMPONENT ---
export const DiaryCalendarView = ({ 
  meetings = [],
  plannerItems = [],
  onSelectMeeting, 
  onCreateMeeting,
  onNavigate,  // To navigate to PLANNER view
  onCreatePlannerItem  // Callback to create planner with context
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(ViewMode.MONTH);
  const [monthDisplayMode, setMonthDisplayMode] = useState(MonthDisplayMode.GRID);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [popupMeeting, setPopupMeeting] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);
  const [showPlannerMenu, setShowPlannerMenu] = useState(false);
  const [plannerMenuPosition, setPlannerMenuPosition] = useState(null);
  const [plannerDateContext, setPlannerDateContext] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);

  // Filter meetings by status
  const filteredMeetings = useMemo(() => {
    if (filterStatus === 'ALL') return meetings;
    return meetings.filter(m => m.status === filterStatus);
  }, [meetings, filterStatus]);

  // Scroll to 8 AM on Week/Day view
  useEffect(() => {
    if ((viewMode === ViewMode.WEEK || viewMode === ViewMode.DAY) && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 480;
    }
  }, [viewMode]);

  // Update current time indicator every minute
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // --- Helper Functions ---
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const addMonths = (date, months) => {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  };

  const formatDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // --- Navigation ---
  const handlePrev = () => {
    if (viewMode === ViewMode.MONTH) setCurrentDate(addMonths(currentDate, -1));
    else if (viewMode === ViewMode.WEEK) setCurrentDate(addDays(currentDate, -7));
    else setCurrentDate(addDays(currentDate, -1));
  };

  const handleNext = () => {
    if (viewMode === ViewMode.MONTH) setCurrentDate(addMonths(currentDate, 1));
    else if (viewMode === ViewMode.WEEK) setCurrentDate(addDays(currentDate, 7));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const handleToday = () => setCurrentDate(new Date());

  // --- Header Title ---
  const getHeaderTitle = () => {
    if (viewMode === ViewMode.MONTH) {
      return currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    } else if (viewMode === ViewMode.WEEK) {
      const start = getStartOfWeek(currentDate);
      const end = addDays(start, 6);
      const startStr = start.toLocaleString('default', { month: 'short', day: 'numeric' });
      const endStr = end.toLocaleString('default', { month: 'short', day: 'numeric' });
      const year = start.getFullYear();
      return `${startStr} - ${endStr}, ${year}`;
    } else {
      return currentDate.toLocaleString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  // --- Meeting Colors ---
  const getMeetingColorClass = (status, type) => {
    const baseColors = {
      [MeetingStatus.ACTIVE]: 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200',
      [MeetingStatus.COMPLETED]: 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200',
      [MeetingStatus.PLANNED]: type === 'VIRTUAL' 
        ? 'bg-purple-100 border-purple-300 text-purple-800 hover:bg-purple-200'
        : 'bg-indigo-100 border-indigo-300 text-indigo-800 hover:bg-indigo-200',
    };
    return baseColors[status] || baseColors[MeetingStatus.PLANNED];
  };

  // --- Meeting Popup Handler ---
  const handleMeetingClick = (e, meeting) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
    setPopupPosition({
      top: rect.bottom - containerRect.top,
      left: rect.left - containerRect.left + rect.width / 2
    });
    setPopupMeeting(meeting);
  };

  // --- Planner Menu Handler ---
  const handlePlannerMenuOpen = (e, dateStr) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
    setPlannerMenuPosition({
      top: rect.bottom - containerRect.top,
      left: rect.left - containerRect.left
    });
    setPlannerDateContext(dateStr);
    setShowPlannerMenu(true);
  };

  const handlePlannerTypeSelect = (type) => {
    // Navigate to PLANNER view and pass context
    if (onNavigate) {
      onNavigate('PLANNER');
    }
    // Call callback to create planner item with pre-filled date and type
    if (onCreatePlannerItem) {
      onCreatePlannerItem(type, plannerDateContext);
    }
  };

  const handlePlannerItemClick = (item) => {
    // Navigate to PLANNER view
    if (onNavigate) {
      onNavigate('PLANNER');
    }
    // You might want to select/edit the item in planner view
    // This would require additional state management in DiaryTemplate
  };

  // --- Render: Month View GRID ---
  const renderMonthGridView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const todayStr = formatDateStr(new Date());

    const grid = [];
    
    // Empty slots for previous month
    for (let i = 0; i < firstDayOfMonth; i++) {
      const prevDate = new Date(year, month, 1 - (firstDayOfMonth - i));
      grid.push(
        <div key={`empty-${i}`} className="bg-gray-50/50 border-b border-r border-gray-200 min-h-[120px] p-1.5">
          <span className="text-xs text-gray-300">{prevDate.getDate()}</span>
        </div>
      );
    }

    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayMeetings = filteredMeetings.filter(m => m.date === dateStr).sort((a, b) => a.time.localeCompare(b.time));
      const dayPlannerItems = (plannerItems || []).filter(p => p.date === dateStr);
      const isToday = dateStr === todayStr;
      const isWeekend = new Date(year, month, day).getDay() === 0 || new Date(year, month, day).getDay() === 6;

      grid.push(
        <div 
          key={dateStr}
          className={`relative border-b border-r border-gray-200 min-h-[120px] p-1.5 hover:bg-indigo-50/30 transition-colors group ${
            isToday ? 'bg-indigo-50/40' : isWeekend ? 'bg-gray-50/30' : 'bg-white'
          }`}
        >
          <div className="flex justify-between items-start mb-1">
            <div className="flex items-center gap-1">
              <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full transition-colors ${
                isToday ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-700 group-hover:bg-gray-100'
              }`}>
                {day}
              </span>
              {(dayMeetings.length + dayPlannerItems.length) > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded-full font-medium">
                  {dayMeetings.length + dayPlannerItems.length}
                </span>
              )}
            </div>
            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button 
                onClick={(e) => { e.stopPropagation(); onCreateMeeting?.(dateStr); }} 
                className="p-1 text-indigo-600 hover:bg-indigo-100 rounded transition-all"
                title="Add Meeting"
              >
                <Users className="w-3.5 h-3.5" />
              </button>
              <button 
                onClick={(e) => handlePlannerMenuOpen(e, dateStr)} 
                className="p-1 text-emerald-600 hover:bg-emerald-100 rounded transition-all"
                title="Add Planner Item"
              >
                <CalendarPlus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          
          <div className="space-y-1 overflow-hidden">
            {dayMeetings.slice(0, 2).map(m => (
              <div 
                key={m.id}
                onClick={(e) => handleMeetingClick(e, m)}
                className={`text-[11px] px-1.5 py-0.5 rounded border truncate cursor-pointer transition-all ${getMeetingColorClass(m.status, m.type)} ${m.status === MeetingStatus.COMPLETED ? 'line-through opacity-60' : ''}`}
              >
                <Users className="w-3 h-3 inline mr-1" />
                <span className="font-medium opacity-80">{m.time}</span>
                <span className="ml-1">{m.title}</span>
              </div>
            ))}
            {dayPlannerItems.slice(0, 2).map(p => (
              <div 
                key={p.id}
                onClick={() => handlePlannerItemClick(p)}
                className="text-[11px] px-1.5 py-0.5 rounded border bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 truncate cursor-pointer transition-all"
              >
                <CalendarPlus className="w-3 h-3 inline mr-1" />
                <span>{p.title}</span>
              </div>
            ))}
            {(dayMeetings.length + dayPlannerItems.length) > 4 && (
              <div className="text-[10px] text-gray-500 font-medium px-1.5">
                +{dayMeetings.length + dayPlannerItems.length - 4} more
              </div>
            )}
          </div>
        </div>
      );
    }

    // Fill remaining cells for next month
    const remainingCells = 42 - grid.length;
    for (let i = 1; i <= remainingCells && grid.length < 42; i++) {
      grid.push(
        <div key={`next-${i}`} className="bg-gray-50/50 border-b border-r border-gray-200 min-h-[120px] p-1.5">
          <span className="text-xs text-gray-300">{i}</span>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 shrink-0">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
            <div key={d} className={`py-2 text-center text-xs font-semibold uppercase ${i === 0 || i === 6 ? 'text-gray-400' : 'text-gray-500'}`}>
              {d}
            </div>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-7 border-l border-gray-200">
            {grid}
          </div>
        </div>
      </div>
    );
  };

  // --- Render: Month View LIST ---
  const renderMonthListView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayStr = formatDateStr(new Date());

    const allDays = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayMeetings = filteredMeetings.filter(m => m.date === dateStr).sort((a, b) => a.time.localeCompare(b.time));
      const dayPlannerItems = (plannerItems || []).filter(p => p.date === dateStr);
      const date = new Date(year, month, day);
      const isToday = dateStr === todayStr;
      
      allDays.push({
        day,
        dateStr,
        date,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        isToday,
        meetings: dayMeetings,
        plannerItems: dayPlannerItems
      });
    }

    return (
      <div className="h-full overflow-y-auto">
        {allDays.map(({ day, dateStr, dayName, isToday, meetings, plannerItems }) => (
          <div key={dateStr} className="flex border-b border-gray-200 hover:bg-gray-50 transition-colors">
            {/* Date Column */}
            <div className={`w-32 shrink-0 p-4 border-r border-gray-200 ${isToday ? 'bg-indigo-50' : 'bg-gray-50'}`}>
              <div className={`text-3xl font-bold ${isToday ? 'text-indigo-600' : 'text-gray-900'}`}>{day}</div>
              <div className="text-sm text-gray-500">{dayName}</div>
              <div className="flex gap-1 mt-3">
                <button 
                  onClick={() => onCreateMeeting?.(dateStr)} 
                  className="p-1.5 text-indigo-600 hover:bg-indigo-100 rounded transition-all"
                  title="Add Meeting"
                >
                  <Users className="w-4 h-4" />
                </button>
                <button 
                  onClick={(e) => handlePlannerMenuOpen(e, dateStr)} 
                  className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded transition-all"
                  title="Add Planner Item"
                >
                  <CalendarPlus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Items Column */}
            <div className="flex-1 p-4">
              {meetings.length === 0 && plannerItems.length === 0 ? (
                <div className="text-sm text-gray-400 italic">No items scheduled</div>
              ) : (
                <div className="space-y-2">
                  {meetings.map(m => (
                    <div
                      key={m.id}
                      onClick={(e) => handleMeetingClick(e, m)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${getMeetingColorClass(m.status, m.type)}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Users className="w-4 h-4" />
                            <span className="font-semibold">{m.title}</span>
                            <Badge color={
                              m.status === MeetingStatus.PLANNED ? 'blue' :
                              m.status === MeetingStatus.ACTIVE ? 'green' : 'gray'
                            }>{m.status}</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm mt-2">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {m.time} ({m.durationMinutes}m)
                            </div>
                            {m.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {m.location}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {plannerItems.map(p => (
                    <div
                      key={p.id}
                      onClick={() => handlePlannerItemClick(p)}
                      className="p-3 rounded-lg border bg-emerald-50 border-emerald-200 hover:bg-emerald-100 cursor-pointer transition-all"
                    >
                      <div className="flex items-center gap-2">
                        <CalendarPlus className="w-4 h-4 text-emerald-600" />
                        <span className="font-semibold text-emerald-900">{p.title}</span>
                      </div>
                      {p.description && (
                        <p className="text-sm text-emerald-700 mt-1">{p.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Week and Day views remain the same as before...
  const renderTimeGrid = (daysToShow) => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const ROW_HEIGHT = 60;

    return (
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header Row */}
        <div className="flex border-b border-gray-200 bg-gray-50 shrink-0 ml-16">
          {daysToShow.map((date, idx) => {
            const dateStr = formatDateStr(date);
            const isToday = date.toDateString() === new Date().toDateString();
            const dayMeetings = filteredMeetings.filter(m => m.date === dateStr);

            return (
              <div key={idx} className={`flex-1 py-3 text-center border-l border-gray-200 first:border-l-0 min-w-[120px] ${isToday ? 'bg-indigo-50/50' : ''}`}>
                <div className={`text-xs font-semibold uppercase ${isToday ? 'text-indigo-600' : 'text-gray-500'}`}>
                  {date.toLocaleString('default', { weekday: 'short' })}
                </div>
                <div className={`text-xl ${isToday ? 'text-indigo-600 font-bold' : 'text-gray-800 font-light'}`}>
                  {date.getDate()}
                </div>
                {dayMeetings.length > 0 && (
                  <div className="mt-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isToday ? 'bg-indigo-200 text-indigo-700' : 'bg-gray-200 text-gray-600'}`}>
                      {dayMeetings.length} meeting{dayMeetings.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Time Grid Body */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative">
          <div className="flex relative" style={{ minHeight: `${24 * ROW_HEIGHT}px` }}>
            
            {/* Time Labels Column */}
            <div className="w-16 shrink-0 flex flex-col bg-white border-r border-gray-100 sticky left-0 z-20">
              {hours.map(hour => (
                <div key={hour} className="relative" style={{ height: `${ROW_HEIGHT}px` }}>
                  <span className="absolute -top-2.5 right-3 text-xs text-gray-400 font-medium">
                    {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                  </span>
                </div>
              ))}
            </div>

            {/* Days Columns */}
            <div className="flex flex-1 relative">
              {/* Hour lines background */}
              <div className="absolute inset-0 z-0 pointer-events-none">
                {hours.map(h => (
                  <div key={h} className="border-t border-gray-100" style={{ height: `${ROW_HEIGHT}px` }}>
                    <div className="border-t border-gray-50 border-dashed" style={{ marginTop: `${ROW_HEIGHT / 2}px` }} />
                  </div>
                ))}
              </div>

              {daysToShow.map((date, colIndex) => {
                const dateStr = formatDateStr(date);
                const dayMeetings = filteredMeetings.filter(m => m.date === dateStr);
                const isToday = date.toDateString() === new Date().toDateString();

                return (
                  <div 
                    key={colIndex} 
                    className={`flex-1 relative border-l border-gray-100 first:border-l-0 min-w-[120px] z-10 ${isToday ? 'bg-indigo-50/20' : ''}`}
                  >
                    {/* Clickable slots for creation */}
                    {hours.map(h => (
                      <React.Fragment key={h}>
                        <div 
                          className="h-[30px] w-full cursor-pointer hover:bg-indigo-100/40 transition-colors"
                          onClick={() => onCreateMeeting?.(dateStr, `${String(h).padStart(2, '0')}:00`)}
                        />
                        <div 
                          className="h-[30px] w-full cursor-pointer hover:bg-indigo-100/40 transition-colors"
                          onClick={() => onCreateMeeting?.(dateStr, `${String(h).padStart(2, '0')}:30`)}
                        />
                      </React.Fragment>
                    ))}

                    {/* Meetings */}
                    {dayMeetings.map(m => {
                      const [h, min] = m.time.split(':').map(Number);
                      const startMinutes = h * 60 + min;
                      const top = startMinutes;
                      const height = Math.max(m.durationMinutes || 30, 25);

                      return (
                        <div
                          key={m.id}
                          onClick={(e) => handleMeetingClick(e, m)}
                          className={`absolute left-1 right-1 rounded-lg border shadow-sm p-2 cursor-pointer overflow-hidden transition-all hover:z-30 hover:shadow-md hover:scale-[1.02] ${getMeetingColorClass(m.status, m.type)} ${m.status === MeetingStatus.COMPLETED ? 'opacity-60' : ''}`}
                          style={{
                            top: `${top}px`,
                            height: `${height}px`,
                          }}
                        >
                          <div className="font-semibold text-xs leading-tight truncate">{m.title}</div>
                          {height > 35 && (
                            <div className="text-[10px] opacity-80 mt-0.5 flex items-center">
                              <Clock className="w-3 h-3 mr-1 shrink-0" />
                              {m.time}
                            </div>
                          )}
                          {height > 50 && m.location && (
                            <div className="text-[10px] opacity-70 mt-0.5 flex items-center truncate">
                              <MapPin className="w-3 h-3 mr-1 shrink-0" />
                              {m.location}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Current Time Indicator */}
                    {isToday && (
                      <div 
                        className="absolute left-0 right-0 z-30 pointer-events-none"
                        style={{ top: `${currentTime.getHours() * 60 + currentTime.getMinutes()}px` }}
                      >
                        <div className="flex items-center">
                          <div className="w-2.5 h-2.5 bg-red-500 rounded-full shadow-sm" />
                          <div className="flex-1 border-t-2 border-red-500" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };


  const renderWeekView = () => {
    const start = getStartOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return renderTimeGrid(weekDays);
  };

  const renderDayView = () => {
    return renderTimeGrid([currentDate]);
  };

  // Get items for current view period
  const getCurrentViewMeetings = () => {
    if (viewMode === ViewMode.DAY) {
      return filteredMeetings.filter(m => m.date === formatDateStr(currentDate));
    } else if (viewMode === ViewMode.WEEK) {
      const start = getStartOfWeek(currentDate);
      const end = addDays(start, 6);
      return filteredMeetings.filter(m => {
        const d = new Date(m.date);
        return d >= start && d <= end;
      });
    } else {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      return filteredMeetings.filter(m => {
        const d = new Date(m.date);
        return d.getFullYear() === year && d.getMonth() === month;
      });
    }
  };

  const getCurrentViewPlannerItems = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return (plannerItems || []).filter(p => {
      const d = new Date(p.date);
      return d.getFullYear() === year && d.getMonth() === month;
    });
  };

  return (
    <div 
      ref={containerRef}
      className={`flex-1 overflow-y-auto flex-col bg-white ${isFullscreen ? 'fixed inset-0 z-50' : 'h-full'}`}
    >
      {/* Top Control Bar */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between px-4 lg:px-6 py-4 border-b border-gray-200 shrink-0 gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <h2 className="text-xl font-bold text-gray-900 min-w-[200px]">
            {getHeaderTitle()}
          </h2>
          
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button onClick={handlePrev} className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={handleToday} className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-white hover:shadow-sm rounded-md transition-all mx-1">
              Today
            </button>
            <button onClick={handleNext} className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <DayStats 
            meetings={getCurrentViewMeetings()} 
            plannerItems={getCurrentViewPlannerItems()}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="ALL">All Status</option>
            <option value={MeetingStatus.PLANNED}>Planned</option>
            <option value={MeetingStatus.ACTIVE}>Active</option>
            <option value={MeetingStatus.COMPLETED}>Completed</option>
          </select>

          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 p-1 rounded-lg">
            {[ViewMode.MONTH, ViewMode.WEEK, ViewMode.DAY].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all capitalize ${
                  viewMode === mode ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {mode.toLowerCase()}
              </button>
            ))}
          </div>

          {/* Month Display Mode Toggle - Only show in MONTH view */}
          {viewMode === ViewMode.MONTH && (
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setMonthDisplayMode(MonthDisplayMode.GRID)}
                className={`p-1.5 rounded-md transition-all ${
                  monthDisplayMode === MonthDisplayMode.GRID ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Grid View"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setMonthDisplayMode(MonthDisplayMode.LIST)}
                className={`p-1.5 rounded-md transition-all ${
                  monthDisplayMode === MonthDisplayMode.LIST ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Fullscreen Toggle */}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          <Button onClick={() => onCreateMeeting?.(formatDateStr(currentDate))} className="shadow-md shadow-indigo-200">
            <Plus className="w-4 h-4 mr-2" /> Schedule
          </Button>
        </div>
      </div>

      {/* View Content */}
      <div className="flex-1 overflow-hidden relative">
        {viewMode === ViewMode.MONTH && monthDisplayMode === MonthDisplayMode.GRID && renderMonthGridView()}
        {viewMode === ViewMode.MONTH && monthDisplayMode === MonthDisplayMode.LIST && renderMonthListView()}
        {viewMode === ViewMode.WEEK && renderWeekView()}
        {viewMode === ViewMode.DAY && renderDayView()}

        {/* Meeting Popup */}
        {popupMeeting && (
          <MeetingPopup 
            meeting={popupMeeting}
            position={popupPosition}
            onSelect={onSelectMeeting}
            onClose={() => setPopupMeeting(null)}
          />
        )}

        {/* Planner Quick Add Menu */}
        {showPlannerMenu && (
          <PlannerQuickAddMenu
            isOpen={showPlannerMenu}
            position={plannerMenuPosition}
            onClose={() => setShowPlannerMenu(false)}
            onSelect={handlePlannerTypeSelect}
          />
        )}
      </div>
    </div>
  );
};

export default DiaryCalendarView;